<script setup>
import { ref, computed, onMounted, onUnmounted, inject } from 'vue'
import { useStore } from 'vuex'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import TopoProfileGenerateDialog from '@/components/topoprofile/TopoProfileGenerateDialog.vue'
import ImportFromPreviousStageDialog from '@/components/schematisationDetail/ImportFromPreviousStageDialog.vue'
import ActionBar from '@/components/common/ActionBar.vue'
import TopoProfileTableRow from '@/components/topoprofile/TopoProfileTableRow.vue'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'

const { x, y, showMenu, show } = actionMenuMixin()

const props = defineProps({
  dirtyItems: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['add-dirty', 'empty-dirty'])

const store = useStore()

const topHeight = inject('topHeight')

const reduceHeight = computed(() => {
  return topHeight?.value + 60 + 'px'
})

const headers = ref([
  {
    sortable: true,
    title: 'X-coordinate',
    value: 'geometry.coordinates.0',
    width: '25%'
  },
  {
    title: 'Y-coordinate',
    value: 'geometry.coordinates.1',
    width: '25%'
  },
  {
    title: 'Slope',
    width: '14%'
  },
  {
    title: 'Name',
    value: 'properties.name',
    width: '50%'
  }
])

// Vuex state and getters
const items = computed(() => store.state.topoProfilePoint.items)
const writeLoading = computed(() => store.state.topoProfilePoint.writeLoading)
const generateLoading = computed(() => store.state.topoProfilePoint.generateLoading)
const currentTopoProfilePoint = computed(() => store.state.topoProfilePoint.currentItem)
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])
const currentItem = computed(() => store.state.topoProfile.currentItem)
const importDialog = computed(() => store.state.topoProfilePoint.importDialog)
const importLoading = computed(() => store.state.topoProfilePoint.importLoading)
const currentSchematisationStage = computed(() => store.state.stabSchematisationStage.currentItem)
const schematisationStagesToImport = computed(
  () => store.getters['stabSchematisationStage/previousSchematisationStagesHaveTopoProfile']
)

const toggleWriteLoadingTopoProfilePoint = (payload) =>
  store.dispatch('topoProfilePoint/toggleWriteLoading', payload)
const update = () => store.dispatch('topoProfilePoint/update')
const add = (payload) => store.dispatch('topoProfilePoint/add', payload)
const create = () => store.dispatch('topoProfilePoint/create')
const setCurrentItem = (payload) => store.dispatch('topoProfilePoint/setCurrentItem', payload)
const generate = (payload) => store.dispatch('topoProfilePoint/generate', payload)
const deleteItem = (id) => store.dispatch('topoProfilePoint/deleteItem', id)
const emptyDirtyItemsFields = () => store.dispatch('topoProfilePoint/emptyDirtyItemsFields')
const toggleDialog = () => store.dispatch('topoProfilePoint/toggleDialog')
const importTopoProfileFromStage = (payload) =>
  store.dispatch('topoProfilePoint/importTopoProfileFromStage', payload)
const toggleImportDialog = (payload) =>
  store.dispatch('topoProfilePoint/toggleImportDialog', payload)
const fetchUniformLoads = (id) => store.dispatch('uniformLoads/fetchItems', id)


// Methods
const saveChanges = async () => {
  try {
    await update()
    await create()
    await fetchUniformLoads(currentSchematisationStage.value.id)
    emptyDirtyItemsFields()
    emit('empty-dirty')
  } catch (e) {
    toggleWriteLoadingTopoProfilePoint(false)
  }
}

const handleImport = async (sourceSchematisationStageId) => {
  const targetSchematisationStageId = currentSchematisationStage.value.id
  await importTopoProfileFromStage({
    sourceSchematisationStageId,
    targetSchematisationStageId,
    topoProfileId: currentItem.value.id
  })
  toggleImportDialog(false)
}

const handleKeyDown = async ({ key }) => {
  if (!isLocked.value && key === 'Delete' && currentTopoProfilePoint.value) {
    await deleteItem(currentTopoProfilePoint.value.id)
    await setCurrentItem(null)
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>
<template>
  <div v-if="currentItem" class="fill-height">
    <ActionBar
      border-bottom
      class="pa-3"
      :disabled="isLocked"
      @save="saveChanges"
      :loading-save="writeLoading"
      show-add
      @add="add({ item: null, topoProfileId: currentItem.id })"
      :show-generate="!!items"
      :loading-generate="generateLoading"
      @generate="items.length ? toggleDialog() : generate(currentItem.id)"
      show-import
      show-save
      @import="toggleImportDialog(true)"
      :disabled-import="schematisationStagesToImport.length === 0"
    />
    <v-data-table
      fixed-header
      class="overflow-y-auto"
      :height="`calc(100vh - ${reduceHeight})`"
      :items-per-page="-1"
      density="compact"
      :headers="headers"
      hide-default-footer
      :items="items ? items : []"
    >
      <template v-slot:item="{ item, index }">
        <TopoProfileTableRow
          :index="index"
          :item="item"
          :dirty-items="dirtyItems"
          @show="isLocked || show($event)" 
          @add-dirty="(...args) => emit('add-dirty', ...args)"
        />
      </template>
    </v-data-table>

    <TopoProfileGenerateDialog></TopoProfileGenerateDialog>
    <ImportFromPreviousStageDialog
      :schematisation-stages="schematisationStagesToImport"
      v-model="importDialog"
      :loading="importLoading"
      text="topographical profile"
      @close="toggleImportDialog(false)"
      @import="handleImport"
    />
    <TableActionMenu
      @update:show-menu="showMenu = $event"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      @action="deleteItem(currentTopoProfilePoint.id)"
      @add="add({ item: currentTopoProfilePoint, topoProfileId: currentItem.id })"
    />
  </div>
</template>
